// Nó do ProseMirror → vnode do Vue (spec seção 5). Texto do documento é sempre nó de
// texto, nunca HTML. Cada nó é renderizado isolado: um erro omite só aquele nó.
import { resolveLink } from './link'
import {
	figureProps, fileDownloadProps, galleryImages, imageLinkProps, imageProps, videoProps, youtubeProps,
} from './media'
import { safeColor, safeFontSize, safeLineHeight } from './styleValues'
import { warn } from './warn'

const SIMPLE = { blockquote: 'blockquote', bulletList: 'ul', listItem: 'li', tableRow: 'tr', detailsSummary: 'summary' }
const MARKS = { bold: 'strong', italic: 'em', underline: 'u', strike: 's', code: 'code', subscript: 'sub', superscript: 'sup' }
// Exigem bibliotecas que o site não carrega (KaTeX, base de emojis) ou o domínio no embed (Twitch)
const UNSUPPORTED = new Set([ 'inlineMath', 'blockMath', 'emoji', 'twitch' ])
const ALIGN = new Set([ 'left', 'center', 'right', 'justify' ])
const LIST_TYPES = new Set([ '1', 'a', 'A', 'i', 'I' ])
const LANGUAGE = /^[\w-]+$/
// `fontFamily` fica de fora: o site usa só a Redonda
const TEXT_STYLE = { color: safeColor, backgroundColor: safeColor, fontSize: safeFontSize, lineHeight: safeLineHeight }
// Ids únicos na página para ligar o checkbox da lista de tarefas ao texto do item
let taskCount = 0

// `type` vem do documento: `constructor` ou `toString` não podem achar nada no protótipo
const has = (map, key) => typeof key === 'string' && Object.hasOwn(map, key)
// Para as mensagens: um `type` que não é string pode ter um `toString` que lança
const label = type => (typeof type === 'string' ? type : typeof type)
const read = attrs => (attrs && typeof attrs === 'object' ? attrs : {})
const text = value => (typeof value === 'string' ? value : '')
const align = attrs => (ALIGN.has(attrs.textAlign) ? { textAlign: attrs.textAlign } : undefined)
const span = value => (Number.isInteger(value) && value > 1 ? value : undefined)

const media = (component, toProps) => (h, attrs, content, ctx, type) => {
	const props = toProps(attrs, ctx)
	if (props) return h(component, { props })
	warn(`Bloco ${type} inválido; ele foi omitido.`)
	return null
}

const NODES = {
	paragraph: (h, attrs, content) => h('p', { style: align(attrs) }, content()),
	heading: (h, attrs, content) => {
		const level = Number.isInteger(attrs.level) && attrs.level >= 1 && attrs.level <= 6 ? attrs.level : 1
		// Um nível abaixo: o h1 da página é o título da notícia
		return h(`h${Math.min(level + 1, 6)}`, { class: `level-${level}`, style: align(attrs) }, content())
	},
	hardBreak: h => h('br', {}),
	horizontalRule: h => h('hr', {}),
	orderedList: (h, attrs, content) => h('ol', {
		attrs: {
			start: Number.isInteger(attrs.start) && attrs.start > 1 ? attrs.start : undefined,
			type: LIST_TYPES.has(attrs.type) ? attrs.type : undefined,
		},
	}, content()),
	// Com rolagem horizontal, `pre` e tabela precisam de foco para rolar pelo teclado
	codeBlock: (h, attrs, content) => h('pre', { attrs: { tabindex: 0 } }, [
		h('code', { class: typeof attrs.language === 'string' && LANGUAGE.test(attrs.language) ? `language-${attrs.language}` : undefined }, content()),
	]),
	table: (h, attrs, content) => h('div', { class: 'table', attrs: { tabindex: 0, role: 'region', 'aria-label': 'Tabela' } }, [ h('table', {}, [ h('tbody', {}, content()) ]) ]),
	tableHeader: (h, attrs, content) => h('th', { attrs: { colspan: span(attrs.colspan), rowspan: span(attrs.rowspan) } }, content()),
	tableCell: (h, attrs, content) => h('td', { attrs: { colspan: span(attrs.colspan), rowspan: span(attrs.rowspan) } }, content()),
	taskList: (h, attrs, content) => h('ul', { class: 'task-list' }, content()),
	// O texto do item dá nome ao checkbox
	taskItem: (h, attrs, content) => {
		const id = `tiptap-task-${++taskCount}`
		return h('li', { class: 'task-item' }, [
			h('input', { attrs: { type: 'checkbox', disabled: true, 'aria-labelledby': id }, domProps: { checked: attrs.checked === true } }),
			h('div', { attrs: { id } }, content()),
		])
	},
	details: (h, attrs, content) => h('details', { attrs: { open: attrs.open === true } }, content()),
	detailsContent: (h, attrs, content) => h('div', { class: 'details-content' }, content()),
	mention: (h, attrs) => {
		const label = text(attrs.label) || text(attrs.id)
		return label ? h('span', { class: 'mention' }, [ `@${label}` ]) : null
	},
	figure: media('TiptapFigure', figureProps),
	image: media('TiptapFigure', imageProps),
	video: media('TiptapVideo', videoProps),
	youtube: media('TiptapVideo', youtubeProps),
	fileDownload: media('TiptapFileDownload', (attrs, ctx) => fileDownloadProps(attrs, ctx.apiRoute)),
	// Sem href ou com href reprovado, vira figure sem legenda
	imageLink: (h, attrs, content, ctx, type) => {
		const props = imageLinkProps(attrs, ctx.origin)
		if (!props) {
			warn(`Bloco ${type} inválido; ele foi omitido.`)
			return null
		}
		if (props.link) return h('TiptapImageLink', { props })
		if (typeof attrs.href === 'string' && attrs.href.trim()) warn('Link da imagem reprovado; exibida sem link.')
		return h('TiptapFigure', { props: figureProps({ src: props.src, alt: props.alt }) })
	},
	// Itens inválidos saem; 1 item vira figure; nenhum, nada
	gallery: (h, attrs, content, ctx, type) => {
		const images = galleryImages(attrs)
		if (!images) {
			warn(`Bloco ${type} inválido; ele foi omitido.`)
			return null
		}
		if (images.length < attrs.images.length) warn('Imagens da galeria com endereço reprovado foram omitidas.')
		if (!images.length) {
			warn('Galeria sem imagens válidas; ela foi omitida.')
			return null
		}
		if (images.length === 1) return h('TiptapFigure', { props: figureProps(images[0]) })
		return h('TiptapGallery', { props: { images } })
	},
}

const textStyle = (attrs) => {
	const style = {}
	Object.keys(TEXT_STYLE).forEach((key) => {
		const value = attrs[key]
		if (value == null || value === '') return
		const safe = TEXT_STYLE[key](value)
		if (safe) style[key] = safe
		else warn(`Estilo inválido em textStyle.${key}; descartado.`)
	})
	return Object.keys(style).length ? style : null
}

const renderLink = (h, attrs, child, ctx) => {
	const link = resolveLink(attrs.href, ctx.origin)
	if (!link) {
		warn('Link com endereço reprovado; exibido só o texto.')
		return child
	}
	const title = text(attrs.title) || undefined
	if (link.kind === 'router') return h('router-link', { class: 'link', props: { to: link.to }, attrs: { title } }, [ child ])
	if (link.kind === 'external') return h('a', { class: 'link', attrs: { href: link.href, title, target: '_blank', rel: 'noopener noreferrer' } }, [ child ])
	return h('a', { class: 'link', attrs: { href: link.href, title } }, [ child ])
}

const renderMark = (h, mark, child, ctx) => {
	const type = mark?.type
	const attrs = read(mark?.attrs)
	if (has(MARKS, type)) return h(MARKS[type], {}, [ child ])
	if (type === 'link') return renderLink(h, attrs, child, ctx)
	if (type === 'textStyle') {
		const style = textStyle(attrs)
		return style ? h('span', { style }, [ child ]) : child
	}
	if (type === 'highlight') {
		const color = safeColor(attrs.color)
		return h('mark', color ? { style: { backgroundColor: color } } : {}, [ child ])
	}
	warn(`Marca desconhecida: ${label(type)}; o texto aparece sem ela.`)
	return child
}

// A primeira marca do JSON fica por fora
const renderText = (h, node, ctx) => {
	const value = text(node.text)
	if (!value) return null
	const marks = Array.isArray(node.marks) ? node.marks : []
	return marks.reduceRight((child, mark) => renderMark(h, mark, child, ctx), value)
}

export const renderNode = (h, node, ctx) => {
	let type
	try {
		type = node && typeof node === 'object' ? node.type : undefined
		if (type === 'text') return renderText(h, node, ctx)
		if (has(SIMPLE, type)) return h(SIMPLE[type], {}, renderNodes(h, node.content, ctx))
		if (has(NODES, type)) return NODES[type](h, read(node.attrs), () => renderNodes(h, node.content, ctx), ctx, type)
		if (UNSUPPORTED.has(type)) warn(`Nó não suportado no site: ${type}.`)
		else warn(`Nó desconhecido: ${label(type)}.`)
	} catch (error) {
		warn(`Erro ao renderizar o nó ${label(type)}; ele foi omitido.`, error instanceof Error ? error.message : undefined)
	}
	return null
}

export const renderNodes = (h, nodes, ctx) => (Array.isArray(nodes)
	? nodes.map(node => renderNode(h, node, ctx)).filter(Boolean)
	: [])
